import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { boards as boardsApi, cards as cardsApi, templates as templatesApi, type Board, type Card, type WorkflowPhase } from '../lib/api';

export interface UseAllBoardsWithCardsResult {
  boards: Board[];
  boardsById: Map<string, Board>;
  allCards: Card[];
  workflowsByBoardId: Record<string, WorkflowPhase[] | undefined>;
  /** Board ids that failed to load cards — page can render a retry pill for each. */
  failedBoardIds: string[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fans out to `GET /boards` + `GET /boards/:id/cards` for every owned board
 * and also pulls each board's template workflow so the Command Center can
 * color phases + flag auto-advance. Partial failures are isolated per board.
 */
export function useAllBoardsWithCards(): UseAllBoardsWithCardsResult {
  const boardsQuery = useQuery({
    queryKey: ['boards'],
    queryFn: boardsApi.list,
  });

  const boardList = useMemo<Board[]>(() => boardsQuery.data?.data ?? [], [boardsQuery.data]);

  const cardQueries = useQueries({
    queries: boardList.map((b) => ({
      queryKey: ['cards', b.id],
      queryFn: () => cardsApi.list(b.id),
      staleTime: 30_000,
      refetchInterval: 30_000,
    })),
  });

  const uniqueTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const b of boardList) {
      const t = b.settings?.defaultTemplate;
      if (t) ids.add(t);
    }
    return Array.from(ids);
  }, [boardList]);

  const templateQueries = useQueries({
    queries: uniqueTemplateIds.map((id) => ({
      queryKey: ['templates', id],
      queryFn: () => templatesApi.get(id),
      staleTime: 5 * 60_000,
    })),
  });

  const boardsById = useMemo(() => {
    const m = new Map<string, Board>();
    for (const b of boardList) m.set(b.id, b);
    return m;
  }, [boardList]);

  const allCards = useMemo<Card[]>(() => {
    const out: Card[] = [];
    for (const q of cardQueries) {
      const data = q.data?.data;
      if (data) out.push(...data);
    }
    return out;
  }, [cardQueries]);

  const workflowsByBoardId = useMemo<Record<string, WorkflowPhase[] | undefined>>(() => {
    const byTemplateId = new Map<string, WorkflowPhase[] | undefined>();
    templateQueries.forEach((q, i) => {
      const id = uniqueTemplateIds[i];
      byTemplateId.set(id, q.data?.data?.workflow);
    });
    const out: Record<string, WorkflowPhase[] | undefined> = {};
    for (const b of boardList) {
      const t = b.settings?.defaultTemplate;
      out[b.id] = t ? byTemplateId.get(t) : undefined;
    }
    return out;
  }, [templateQueries, uniqueTemplateIds, boardList]);

  const failedBoardIds = useMemo(
    () =>
      boardList
        .map((b, i) => ({ b, q: cardQueries[i] }))
        .filter(({ q }) => q?.isError)
        .map(({ b }) => b.id),
    [boardList, cardQueries],
  );

  const isLoading = boardsQuery.isLoading || cardQueries.some((q) => q.isLoading);
  const isError = boardsQuery.isError;

  return {
    boards: boardList,
    boardsById,
    allCards,
    workflowsByBoardId,
    failedBoardIds,
    isLoading,
    isError,
  };
}
